import { useSelector } from '@legendapp/state/react';
import type { BackendTypes } from '@tf/api';
import type { TFEmptyStateMessageProps } from '@tf/ui';
import {
	ActionIcon,
	Box,
	Button,
	Center,
	CopyButton,
	createStyles,
	Flex,
	Icon,
	rem,
	TFCard,
	TFDataTable,
	TFEmptyStateMessage,
	TFText,
	Tooltip,
	UnstyledButton,
	useDebouncedValue,
	useLocalStorage,
	usePersistentPagination,
} from '@tf/ui';
import { D, fmt, S } from '@tf/utils';
import type React from 'react';
import { Link } from 'react-router-dom';
import { HiddenAccountsNotice, ReviewState, TFBadge, TFBadgeColors } from '@/components/shared';
import { getProfilesTableFilters } from '@/components/utils';
import { useOverviewAccounts } from '@/core/api/overview';
import { appStore } from '@/core/stores';

import { AssignedAccountManagersWidget } from '../../shared/AssignedManagers';

import { AccountPriorityWidget } from './AccountPriorityWidget';
import { AccountStatus } from './AccountStatus';

interface ProfilesTableProps {
	filterByKinds?: string[];
	storageKeyPrefix?: string;
	emptyStateIcon?: React.ReactElement;
	emptyStateTitle?: string;
	emptyStatePrimaryAction?: React.ReactNode;
	emptyStateSecondaryAction?: React.ReactNode;
	searchPlaceholder?: string;
	columnLabels?: {
		name?: string;
		type?: string;
		status?: string;
	};
}

const riskLevelColors: Record<BackendTypes.OverviewAccountRiskLevel, TFBadgeColors> = {
	VERY_HIGH: TFBadgeColors.RED,
	HIGH: TFBadgeColors.ORANGE,
	MEDIUM: TFBadgeColors.YELLOW,
	LOW: TFBadgeColors.GREEN,
};

const useStyles = createStyles(({ colors }) => ({
	profileName: {
		fontSize: 13,
		height: 32,
		display: 'flex',
		alignItems: 'center',
		color: colors.brand[6],
		'&:hover': {
			color: colors.brand[6],
			textDecoration: 'underline',
		},
	},
	profileNameText: {
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		maxWidth: 300,
	},
	copyableCell: {
		display: 'flex',
		alignItems: 'center',
		gap: rem(4),
		userSelect: 'text',
		'& .copy-button': {
			opacity: 0,
			transition: 'opacity 0.2s ease',
		},
		'&:hover .copy-button': {
			opacity: 1,
		},
	},
}));

const filtersNamesMap: Record<string, string> = {
	kind: 'accountType',
	'values.accountStatus': 'accountStatus',
	verification: 'verificationStatus',
	importance: 'priority',
	assignedTo: 'assignedTo',
};

const sortingNamesMap: Record<string, string> = {
	name: 'ACCOUNT_NAME',
	kind: 'ACCOUNT_TYPE',
	'values.accountStatus': 'ACCOUNT_STATUS',
	verification: 'VERIFICATION_STATUS',
	importance: 'PRIORITY',
	'values.nextKycReviewDate': 'NEXT_KYC_REVIEW',
	statusChangedAt: 'ACCOUNT_STATUS_CHANGED_AT',
};

const CopyableCell = ({ value }: { value?: string | null }) => {
	const { classes } = useStyles();

	if (!value) {
		return <TFText>—</TFText>;
	}

	return (
		<Flex className={classes.copyableCell}>
			<TFText>{value}</TFText>
			<CopyButton value={value} timeout={2000}>
				{({ copied, copy }) => (
					<Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="top">
						<ActionIcon
							className="copy-button"
							h="90%"
							color={copied ? 'teal' : 'gray'}
							variant="transparent"
							onClick={copy}
						>
							{copied ? <Icon.IconCheck style={{ width: rem(16) }} /> : <Icon.IconCopy style={{ width: rem(16) }} />}
						</ActionIcon>
					</Tooltip>
				)}
			</CopyButton>
		</Flex>
	);
};

export const ProfilesTable = ({
	filterByKinds,
	storageKeyPrefix = 'account',
	emptyStateIcon = <Icon.IconUserOff />,
	emptyStateTitle = 'No accounts found',
	emptyStatePrimaryAction,
	emptyStateSecondaryAction,
	searchPlaceholder,
	columnLabels = {},
}: ProfilesTableProps) => {
	const { classes } = useStyles();
	const accountKinds = useSelector(() => appStore.defs.options.AccountKind.get());

	const labels = {
		name: columnLabels.name ?? 'Account name',
		type: columnLabels.type ?? 'Account type',
		status: columnLabels.status ?? 'Account status',
	};

	const getStorageKey = (suffix: string) => `${storageKeyPrefix}-${suffix}`;

	const [pagination, setPagination] = usePersistentPagination(getStorageKey('table'), {
		pageSize: 10,
		pageIndex: 0,
	});

	const [searchValue, setSearchValue] = useLocalStorage<string>({
		defaultValue: '',
		key: getStorageKey('table-search'),
		getInitialValueInEffect: false,
	});
	// TODO: move this logic to TFDataTable component. It should:
	//		- apply debounce for server search updates (now it' duplicated in all parent components)
	//		- save and restore filters and search in LS (now table and parent components both do it)

	const [debouncedSearch] = useDebouncedValue(searchValue, 500);

	const [filtersState, setFiltersState] = useLocalStorage<
		{
			id: string;
			value: unknown;
		}[]
	>({
		defaultValue: [],
		key: getStorageKey('table-filters'),
		getInitialValueInEffect: false,
	});

	const [sortingState, setSortingState] = useLocalStorage<
		{
			desc: boolean;
			id: string;
		}[]
	>({
		defaultValue: [{ desc: true, id: 'name' }],
		key: getStorageKey('table-sorting'),
		getInitialValueInEffect: false,
	});

	const sortingParams =
		sortingState.length > 0
			? {
					sortBy: sortingNamesMap[sortingState[0].id] as BackendTypes.OverviewSortBy | undefined,
					sortOrder:
						sortingState[0].id === 'statusChangedAt'
							? sortingState[0].desc
								? 'ASC'
								: 'DESC'
							: sortingState[0].desc
								? 'DESC'
								: ('ASC' as BackendTypes.OverviewSortOrder),
				}
			: undefined;

	const filteringParams = filtersState.reduce<Record<string, any>>((acc, curr) => {
		acc[filtersNamesMap[curr.id]] = curr.value;
		return acc;
	}, {});

	const { data, isPending, isError } = useOverviewAccounts(pagination.pageIndex + 1, pagination.pageSize, {
		...filteringParams,
		...sortingParams,
		...(filterByKinds ? { accountTypes: filterByKinds } : {}),
		query: searchValue === '' ? searchValue : debouncedSearch,
	});

	const accounts = data?.accounts;
	const listUsers = data?.listUsers ?? [];

	const hasArchivedStatusMessage = Boolean(!filtersState.find((f) => f.value === 'ARCHIVED') && !!debouncedSearch);

	const refreshPageButton = (
		<Button
			variant="light"
			size="md"
			leftSection={<Icon.IconRefresh size={18} />}
			onClick={() => window.location.reload()}
		>
			Refresh the page
		</Button>
	);

	const errorMessage: TFEmptyStateMessageProps = {
		icon: <Icon.IconUnlink />,
		title: 'Something went wrong',
		description: 'Try to refresh the page or come back later',
		primaryAction: refreshPageButton,
	};

	return (
		<>
			{isError ? (
				<TFCard>
					<Center h={300}>
						<TFEmptyStateMessage {...errorMessage} />
					</Center>
				</TFCard>
			) : (
				<TFDataTable<BackendTypes.OverviewAccount>
					noFilteredDataMessage={{
						icon: emptyStateIcon,
						title: emptyStateTitle,
					}}
					noDataMessage={{
						icon: emptyStateIcon,
						title: emptyStateTitle,
						primaryAction: emptyStatePrimaryAction,
						secondaryAction: emptyStateSecondaryAction,
					}}
					name={`${storageKeyPrefix}-list`}
					isLoading={isPending}
					serverSorting={{
						onSortingChange: setSortingState,
						state: sortingState,
					}}
					serverFiltering={{
						onFiltersChange: setFiltersState,
						state: filtersState,
					}}
					serverPagination={
						accounts
							? {
									...pagination,
									rowCount: accounts.count,
									onPaginationChange: setPagination,
								}
							: undefined
					}
					serverSearch={{
						state: searchValue,
						onSearchChange: setSearchValue,
					}}
					searchPlaceholder={searchPlaceholder}
					data={accounts?.items ?? []}
					defs={[
						{
							header: labels.name,
							accessorKey: 'name',
							size: 200,
							Cell: ({ cell }) => {
								const { nodeId, name } = cell.row.original;
								return (
									<UnstyledButton component={Link} to={`/accounts/${nodeId}`} className={classes.profileName}>
										<Tooltip label={name || 'N/A'} position="top">
											<TFText inherit className={classes.profileNameText}>
												{name || 'N/A'}
											</TFText>
										</Tooltip>
									</UnstyledButton>
								);
							},
						},
						{
							header: labels.type,
							accessorKey: 'kind',
							enableSorting: !filterByKinds,
							Cell: ({ cell }) => {
								const nextKind = accountKinds?.find((a: { value: string }) => a.value === cell.getValue());
								return (
									<TFText inherit lineClamp={1}>
										{nextKind?.label || 'Not defined'}
									</TFText>
								);
							},
						},
						{
							header: labels.status,
							id: 'values.accountStatus',
							accessorKey: 'values.accountStatus',
							sortUndefined: 1,
							Cell: ({ row }: { row: { original: BackendTypes.OverviewAccount } }) => {
								return <AccountStatus accountStatus={row.original.values?.accountStatus} />;
							},
						},
						{
							header: 'Time in status',
							id: 'statusChangedAt',
							accessorKey: 'statusChangedAt',
							Cell: ({ row }: { row: { original: BackendTypes.OverviewAccount } }) => {
								const changedAt = row.original.statusChangedAt;
								return (
									<Tooltip label={`Since ${fmt.toDate(changedAt, { preset: 'full_date_with_time' })}`} position="top">
										<div>{D.calculateTillNow(changedAt)}</div>
									</Tooltip>
								);
							},
						},
						{
							header: 'Verification status',
							accessorKey: 'verification',
							Cell: ({ cell }) => <ReviewState account={cell.row.original} />,
						},
						{
							header: 'Assigned managers',
							accessorKey: 'assignedManagers',
							enableSorting: false,
							Cell: (data: { row: { original: { nodeId: number; assignedManagers: string[] } } }) => (
								<AssignedAccountManagersWidget
									accountId={data.row.original.nodeId}
									selectedIds={data.row.original.assignedManagers}
									managers={listUsers ?? []}
								/>
							),
						},
						{
							header: 'E-mail',
							id: 'applicantEmail',
							accessorKey: 'applicantEmail',
							enableSorting: false,
							sortUndefined: 1,
							minSize: 240,
							Cell: ({ row }: { row: { original: any } }) => {
								return <CopyableCell value={row.original.applicantEmail} />;
							},
						},
						{
							header: 'Phone',
							id: 'applicantPhone',
							enableSorting: false,
							accessorKey: 'applicantPhone',
							sortUndefined: 1,
							minSize: 180,
							Cell: ({ row }: { row: { original: any } }) => {
								return <CopyableCell value={row.original.applicantPhone} />;
							},
						},
						{
							header: 'Priority',
							accessorKey: 'importance',
							Cell: ({ row }: { row: { original: BackendTypes.OverviewAccount } }) => (
								<AccountPriorityWidget accountId={row.original.nodeId} value={row.original.importance} />
							),
						},
						{
							header: 'Risk profile',
							accessorKey: 'values.riskLevel',
							enableSorting: false,
							Cell: ({ row }: { row: { original: BackendTypes.OverviewAccount } }) => {
								const riskLevel = row.original.values.riskLevel;
								return (
									<TFBadge
										withBackground={false}
										text={riskLevel ? S.prettify(riskLevel) : 'Not set'}
										color={riskLevel ? riskLevelColors[riskLevel] : TFBadgeColors.DIMMED}
									/>
								);
							},
						},
						{
							header: 'Next KYC review',
							id: 'values.nextKycReviewDate',
							accessorKey: 'values.nextKycReviewDate',
							enableSorting: true,
							sortDescFirst: false,
							sortUndefined: 'last',
							Cell: ({ row }: { row: { original: BackendTypes.OverviewAccount } }) => {
								const date: string | undefined = row.original.values.nextKycReviewDate;
								const status = row.original.values.accountStatus;
								if (!date || status === 'REJECTED' || status === 'CLOSED') {
									return (
										<TFText inherit c="dimmed">
											Not defined
										</TFText>
									);
								}
								const dateColor = D.getDateDisplayColor(date);
								const dateDisplayText = D.getDateDisplayText(date);

								return <TFText c={dateColor}>{dateDisplayText}</TFText>;
							},
						},
					]}
					availableFilters={getProfilesTableFilters(listUsers, accountKinds, filterByKinds)}
				/>
			)}
			<Box mt="12px">
				{hasArchivedStatusMessage && (
					<HiddenAccountsNotice
						textSize="13px"
						withIcon
						onClick={() => setFiltersState([{ id: 'values.accountStatus', value: 'ARCHIVED' }])}
					/>
				)}
			</Box>
		</>
	);
};
